import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Buttons, GLOBAL_IMAGES } from "../../assets/asset";
import { AlertCircle } from "lucide-react";

import { SkeletonYears } from "../../utils/SkeletonLoader";
import Header from "./YearsHeader";

import { useDispatch, useSelector } from "react-redux";
import { setActiveYear } from "../../store/slice/scrollPositionSlice";

import { useYears } from "../../features/queryHooks/useYears";
import { useUpdateUser } from "../../features/auth/useLogin";
import { toast } from "react-toastify";
import BigPrimaryButton from "../ui/BigPrimaryButton";
import {
  setCurrentYear,
  setShowKnowledge,
} from "../../store/slice/profileSlice";
import { content } from "../../utils/constants";
import DemoManager from "./DemoManager";
import { logout } from "../../features/auth/user.reducer";

const YearsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let str = "i am a string";

  const { user, isNewUser } = useSelector((state) => state.user);

  const [showLessonContent, setShowLessonContent] = React.useState(null);
  const [showYearModal, setShowYearModal] = React.useState(null);
  const [showUnpaidPopup, setShowUnpaidPopup] = useState(false);

  // Check isPaid status and show popup if not paid
  const DEV = true;
  useEffect(() => {
    if (DEV) return;
    if (user?.isPaid !== undefined) {
      const isPaidStatus = user.isPaid === "1" || user.isPaid === 1;
      console.log("User isPaid status:", user.isPaid);
      console.log("Is paid user:", isPaidStatus);

      if (!isPaidStatus) {
        setShowUnpaidPopup(true);
      }
    }
  }, [user?.isPaid]);

  // Use the DemoManager component
  const { showDemo, demoCompleted, handleDemo, DemoComponent } = DemoManager({
    isNewUser,
  });

  const yearsQuery = useYears();
  const updateUserMutation = useUpdateUser();

  useEffect(() => {
    dispatch(setActiveYear(-1));
  }, []);

  // Helper function to determine if a year is locked
  const isYearLocked = (year) => {
    // Check if isOpen field exists and is 0 (locked)
    return year.fields.isOpen === 0 || year.fields.isOpen === "0";
  };

  const newUpdateUserYearSelection = async (yearId) => {
    updateUserMutation.mutate(
      { id: user?.UserId || user.pk, yearid: yearId },
      {
        onSuccess: () => {
          navigate("/lessonDashboard", { state: { yearId } });
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ||
              "Error updating user year. Please try again.",
            {
              ariaLabel: "Update Error",
              autoClose: 2000,
              theme: "colored",
              hideProgressBar: true,
              className: "font-mali",
            }
          );
        },
      }
    );
  };

  const handleLessonClick = () => {
    newUpdateUserYearSelection(showLessonContent);
    dispatch(setShowKnowledge(true));
    setShowLessonContent(false);
  };

  const handleYearClick = (year) => {
    // Check if year is locked before allowing click
    if (isYearLocked(year)) {
      // toast.warning(`${year.fields.year} is currently locked and not available.`, {
      //   ariaLabel: "Year Locked",
      //   autoClose: 2000,
      //   theme: "colored",
      //   hideProgressBar: true,
      //   className: "font-mali",
      // });
      return;
    }

    if (demoCompleted) {
      // Navigate to DemoDashboard with year ID if demo was just completed
      navigate("/demoDashboard", { state: { yearId: year.pk } });
    } else {
      // Original behavior - show lesson content
      setShowLessonContent(year.pk);
    }
    dispatch(setCurrentYear(year));
  };

  const handleUnpaidPopupClose = () => {
    setShowUnpaidPopup(false);
    dispatch(logout());
    navigate("/login");
  };

  return (
    <>
      <div
        className="min-h-screen  flex flex-col items-center bg-blue-50 font-mali overflow-auto"
        style={{
          backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        role="main"
        aria-labelledby="years-dashboard-title"
      >
        {showLessonContent && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/30 overflow-y-auto">
            <div
              style={{
                backgroundImage: `url(${GLOBAL_IMAGES.contentCardImage})`,
              }}
              className=" bg-contain bg-center bg-no-repeat 
        flex flex-col items-center justify-center 
        
    
        min-h-[400px] lg:min-h-[400px] max-w-2xl w-full 2xl:min-h-[650px]"
            >
              {" "}
              <div
                className="text-center xl:w-7/12 w-6/12 2xl:w-8/12  
             xl:h-[25rem] h-[20rem] lg:mt-10 xl:mt-10 
             2xl:mt-2 mb-36 xl:mb-40 flex flex-col my-auto content-fix"
              >
                <h2 className="text-center mb-4 w-full text-xl xl:text-3xl font-bold text-primary-dark-blue">
                  CONTENTS
                </h2>
                <div className="overflow-y-auto gap-2 flex flex-col text-gold">
                  {content[showLessonContent - 1]?.map((item, index) => (
                    <div
                      key={index}
                      className=" bg-primary-button-dark-blue rounded-full "
                    >
                      <p className="text-[#f4b331] text-nowrap font-semibold mx-2 my-1">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-center  w-full">
              <button
                disabled={updateUserMutation.isPending}
                className="bg-primary-button-dark-blue  lg:px-10 rounded-3xl lg:mt-4 py-2 border-4 border-yellow-400"
                onClick={handleLessonClick}
              >
                <span className="font-mali font-bold text-xl lg:text-2xl text-white">
                  {!updateUserMutation.isPending ? "Lets Go" : "Wait..."}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Render the demo component */}
        {DemoComponent}

        {/* Header */}
        <div className="w-full">
          <Header showDemo={showDemo} showLessonContent={showLessonContent} />
        </div>

        {/* Main Content */}
        <div
          className="flex mb-8 flex-col items-center justify-center gap-2 self-center space-y-8 w-full px-11"
          aria-describedby="years-dashboard-description"
        >
          <h1
            id="years-dashboard-title"
            className="text-black text-center text-4xl md:text-5xl font-bold leading-none"
          >
            Select {user.user_name}'s Math grade
          </h1>
          <p
            id="years-dashboard-description"
            className="text-black text-center font-bold leading-none text-lg md:text-2xl"
          >
            We will curate {user.user_name}'s learning experience based on the
            grade and assessment
          </p>

          {/* Info Box */}
          <div
            className="w-full flex justify-center py-6 gap-5 items-center px-5"
            style={{
              backgroundColor: "#975113",
              maxWidth: "70%",
              borderRadius: "10px",
            }}
            role="region"
            aria-label="Information about grade selection"
          >
            <img
              alt="Exclamation mark icon"
              src={GLOBAL_IMAGES.exclamationMark}
              className="h-9 self-center"
            />
            <p className="text-white text-center text-sm md:text-xl">
              You can always go back and change the math grade of your child
              from settings, if you feel you need to change the grade.
            </p>
          </div>

          {/* Loading and Error Handling */}
          {yearsQuery.isPending && (
            <SkeletonYears aria-label="Loading years data" />
          )}
          {yearsQuery.isError && (
            <p
              className="text-red-500"
              role="alert"
              aria-live="assertive"
              data-testid="error-message"
            >
              Error: {yearsQuery.error.message}
            </p>
          )}

          {/* Years List */}
          {!yearsQuery.isPending && !yearsQuery.isError && (
            <div
              className={`w-screen overflow-x-scroll py-6 pb-12  overflow-hidden hover:overflow-auto ${
                showDemo === true || showDemo === 1 || !showDemo
                  ? "z-10"
                  : "z-10"
              }`}
              role="list"
              aria-label="List of available years"
            >
              <div className="flex z-40 justify-center space-x-10 mx-9">
                {yearsQuery.data.data.slice(4).map((year, index) => {
                  const isLocked = isYearLocked(year);
                  
                  return (
                    <div
                      key={year.id}
                      className={`size-24 mb-6  md:w-36 md:h-36 lg:size-40 flex relative items-center justify-center rounded-full ${
                        isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                      } ${
                        index % 2 === 0 ? "-top-4" : "top-10"
                      }`}
                      style={{
                        backgroundImage: `url(${GLOBAL_IMAGES.yearContainerImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        flexShrink: 0,
                      }}
                      onClick={() => handleYearClick(year)}
                      role="listitem"
                      aria-label={`Year ${year.fields.year} ${isLocked ? '(locked)' : ''}`}
                      data-testid={`year-${year.pk}`}
                    >
                      {/* Lock Icon - Show when year is locked */}
                      {isLocked && (
                        <img
                          src={GLOBAL_IMAGES.lesson_lock}
                          alt="lock"
                          className="absolute w-12 h-12 md:w-16 md:h-16 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                        />
                      )}
                      
                      <p
                        className={`font-bold text-center text-sm md:text-xl lg:text-2xl ${
                          isLocked ? "text-gray-400" : "text-white"
                        }`}
                        aria-hidden="true"
                      >
                        {year.fields.year}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unpaid User Popup */}
      {showUnpaidPopup && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md mx-4 shadow-2xl border border-gray-200">
            <div className="text-center">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={32} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Subscription Required
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Your account does not have an active subscription. Please login
                again or upgrade to access the full features of MATHDINGO.
              </p>
              <button
                onClick={handleUnpaidPopupClose}
                className="bg-blue-800 text-white px-8 py-3 rounded-xl hover:bg-blue-900 transition duration-300 font-semibold text-lg w-full"
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default YearsDashboard;